import { PDFArray, PDFBool, PDFDict, PDFDocument, PDFHexString, PDFName, PDFPage, PDFRef, PDFString } from "pdf-lib";

// pdf-lib's copyPages carries a page's widget annotations (the visible field boxes with their values)
// but not the document's AcroForm entry, so a copied page showed its fields yet a PDF reader found no
// form: nothing could be typed into them any more. This rebuilds the entry for the fields that are on
// the copied pages, and drops field parts that belong to pages that were left behind.

const tagOf = (ref: PDFRef) => `${ref.objectNumber}:${ref.generationNumber}`;

/** Returns how many top-level form fields the new document now has. */
export function carryFormFields(src: PDFDocument, dst: PDFDocument): number {
  const ctx = dst.context;
  const widgetRefs = new Set<string>();
  const topFields: PDFRef[] = [];
  const seenTop = new Set<string>();

  for (const page of dst.getPages()) {
    const annots = page.node.lookupMaybe(PDFName.of("Annots"), PDFArray);
    if (!annots) continue;
    for (let i = 0; i < annots.size(); i++) {
      const raw = annots.get(i);
      if (!(raw instanceof PDFRef)) continue;
      const dict = ctx.lookupMaybe(raw, PDFDict);
      if (!dict || dict.get(PDFName.of("Subtype"))?.toString() !== "/Widget") continue;
      widgetRefs.add(tagOf(raw));
      let top: PDFRef = raw;
      let cur: PDFDict = dict;
      for (let guard = 0; guard < 32; guard++) {
        const parentRaw = cur.get(PDFName.of("Parent"));
        const parent = parentRaw ? ctx.lookupMaybe(parentRaw, PDFDict) : undefined;
        if (!parent || !(parentRaw instanceof PDFRef)) break;
        top = parentRaw;
        cur = parent;
      }
      if (!seenTop.has(tagOf(top))) {
        seenTop.add(tagOf(top));
        topFields.push(top);
      }
    }
  }
  if (topFields.length === 0) return 0;

  // A field with several boxes (a radio group across pages) keeps only the boxes that are on kept pages.
  const prune = (ref: PDFRef): boolean => {
    const node = ctx.lookupMaybe(ref, PDFDict);
    if (!node) return false;
    const kids = node.lookupMaybe(PDFName.of("Kids"), PDFArray);
    if (!kids) return widgetRefs.has(tagOf(ref));
    const kept: PDFRef[] = [];
    for (let i = 0; i < kids.size(); i++) {
      const k = kids.get(i);
      if (k instanceof PDFRef && prune(k)) kept.push(k);
    }
    node.set(PDFName.of("Kids"), ctx.obj(kept));
    return kept.length > 0;
  };
  const fields = topFields.filter((f) => prune(f));
  if (fields.length === 0) return 0;

  const srcAcro = src.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
  const acro = ctx.obj({}) as PDFDict;
  acro.set(PDFName.of("Fields"), ctx.obj(fields));
  // The default appearance may be a literal or a hex string (re-saved password-protected files use hex), so accept both (QA RM-1).
  const da = srcAcro?.lookupMaybe(PDFName.of("DA"), PDFString, PDFHexString);
  if (da) acro.set(PDFName.of("DA"), PDFString.of(da.decodeText()));
  acro.set(PDFName.of("NeedAppearances"), PDFBool.True);
  dst.catalog.set(PDFName.of("AcroForm"), ctx.register(acro));
  return fields.length;
}

/**
 * A page that was copied a second time carries its own copies of the form fields. Giving each field a
 * new name (name_2, name_3...) keeps the boxes visible and fillable without two boxes sharing one value.
 */
export function renameFieldsOnPage(dst: PDFDocument, page: PDFPage, copyNumber: number): void {
  const ctx = dst.context;
  const annots = page.node.lookupMaybe(PDFName.of("Annots"), PDFArray);
  if (!annots) return;
  const done = new Set<PDFDict>();
  for (let i = 0; i < annots.size(); i++) {
    const raw = annots.get(i);
    const dict = raw instanceof PDFRef ? ctx.lookupMaybe(raw, PDFDict) : undefined;
    if (!dict || dict.get(PDFName.of("Subtype"))?.toString() !== "/Widget") continue;
    let cur: PDFDict = dict;
    for (let guard = 0; guard < 32; guard++) {
      const parentRaw = cur.get(PDFName.of("Parent"));
      const parent = parentRaw ? ctx.lookupMaybe(parentRaw, PDFDict) : undefined;
      if (!parent) break;
      cur = parent;
    }
    if (done.has(cur)) continue;
    done.add(cur);
    const t = cur.lookupMaybe(PDFName.of("T"), PDFString, PDFHexString);
    if (t) cur.set(PDFName.of("T"), PDFHexString.fromText(`${t.decodeText()}_${copyNumber}`));
  }
}
